import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Form, Button, Card, Spinner, Alert, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { DeleteCookie, SetCookie } from "../../common/CookieHelper";
import { UserGateway } from "../../gateways/UserGateway";
import { OrderGateway, OrderResponse } from "../../gateways/OrderGateway";
import { useGlobal } from "../../contexts/GlobalContext";
import { Utils } from "../../common/Utils";
import { SiteSettings } from "../../model/SiteSettingModel";
import { CartItem } from "../../model/CartModel";
import { CountryCodes, UserDetails } from "../../model/UserModel";
import { ProductGateway } from "../../gateways/ProductGateway";
import { CartGateway } from "../../gateways/CartGateway";
import { MessageGateway, MessageType } from "../../gateways/MessageGateway";
import { PaymentGateway, CheckoutRequest, CheckoutResponse } from "../../gateways/PaymentGateway";
import { OrderStatus, PaymentType } from "../../model/OrderModel";
import { SiteCookie } from "../../model/SiteModel";
import { GetCartCookieKey } from "../../gateways/SiteGateway";
import PageVisibilityListener from "../common/PageVisibilityListener";

enum FlagState {
    Show = "show",
    Hide = "hide",
    Complete = "complete",
}

const Checkout: React.FC = () => {
    const { cart, updateCart, user, scannedDeliveryLocation, settings } = useGlobal();
    const [enrichedCart, setEnrichedCart] = useState<CartItem[]>();
    const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneCode, setPhoneCode] = useState("+27");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [initialPhoneNumber, setInitialPhoneNumber] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState<{ firstName?: string, lastName?: string, email?: string; confirmPassword?: string; password?: string; deliveryLocation?: string; phoneNumber?: string; globalError?: string; verifyPin?: string; paymentMethod?: string }>({});
    const [deliveryLocation, setDeliveryLocation] = useState("");
    const [verifyPin, setVerifyPin] = useState("");
    const [comments, setComments] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [deliveryMethod, setDeliveryMethod] = useState("collection");
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [storeStatus, setStoreStatus] = useState<string>();
    const [paymentMethod, setPaymentMethod] = useState<PaymentType>(PaymentType.None);
    const [showModal, setShowModal] = useState<FlagState>(FlagState.Hide);
    const [showSnapScanModal, setShowSnapScanModal] = useState<boolean>(false);
    const [snapScanUrl, setSnapScanUrl] = useState("");
    const [isSnapScanLoading, setIsSnapScanLoading] = useState(false);
    const [messageType, setMessageType] = useState<MessageType>(MessageType.Email);
    const navigate = useNavigate();
    //const { orderId } = useParams();

    useEffect(() => {

        const fetchData = async () => {
            if (user) {
                const userDetails = await UserGateway.GetUser(user);
                if (userDetails) {
                    setUserDetails(userDetails);
                    setFirstName(userDetails.firstName);
                    setLastName(userDetails.lastName);
                    setPhoneNumber(userDetails.phoneNumber ?? '');
                    setInitialPhoneNumber(Utils.FormatPhoneNumber(userDetails.phoneCode ?? "+27", userDetails.phoneNumber ?? ''));
                    setEmail(user.email ?? "");
                    setPhoneCode(userDetails.phoneCode ?? "+27");
                    setMessageType(userDetails.messagePreference);
                }
            } else {
                setUserDetails(null);
            }

            // Fetch product details from Firestore & filter out inactive products
            const enrichedCartPromise = await Promise.all(
                cart.cartItems.map(async (item) => {
                    const productData = await ProductGateway.GetProduct(item.id);
                    if (productData) {
                        // Only include active products in the cart
                        if (productData.status === "Active") {
                            return {
                                ...item, imageUrl: productData.imageUrl, price: Number(productData.price) + Object.entries(item.attributes).reduce((sum, [name, value]) => {
                                    return sum + ProductGateway.GetAttributePrice(productData, name, value);
                                }, 0)
                            };
                        }
                    }
                    return item;
                })
            );
            setEnrichedCart(enrichedCartPromise);
        };

        if (scannedDeliveryLocation) {
            setDeliveryLocation(scannedDeliveryLocation);
        }

        fetchData();
    }, [cart, user, scannedDeliveryLocation]);

    useEffect(() => {

        const checkStoreStatus = (settings: SiteSettings): string => {
            if (!settings || !settings.businessHours || settings.receiveOrdersAlways)
                return "";

            const checkOpen: string = settings.receiveOrdersLoggedIn && !settings.storeOnline ? `Sorry ${settings.siteTitle} is currently not online to receive orders.` : "";

            if (settings.receiveOrdersDuringHours) {
                const now = new Date();
                const currentDay = now.toLocaleDateString("en-US", { weekday: "long" });
                const currentTime = now.toTimeString().slice(0, 5); // Format HH:MM

                const todayHours = settings.businessHours[currentDay];

                if (currentTime >= todayHours.openTime && currentTime < todayHours.closeTime) {
                    return checkOpen;
                } else {
                    return `Sorry, ${settings.siteTitle} is closed for orders. It is open from ${todayHours.openTime} to ${todayHours.closeTime} today.`;
                }
            }

            return checkOpen;
        };

        if (settings) {
            if (!settings.allowEmailLogin && !user) {
                navigate(Utils.GetUrl("/login/checkout"));
                return;
            }

            setStoreStatus(checkStoreStatus(settings));
            setDeliveryFee(settings && settings.allowDelivery && settings.deliveryCost && deliveryMethod !== "collection" ? Number(settings.deliveryCost) : 0);
        }
    }, [settings, navigate, user, deliveryMethod]);

    const validateForm = async () => {
        let newErrors: { firstName?: string, lastName?: string, email?: string; confirmPassword?: string; password?: string, phoneNumber?: string } = {};

        if (firstName === "") {
            newErrors.firstName = "First Name is required";
        }
        if (lastName === "") {
            newErrors.lastName = "Last Name is required";
        }

        if (!phoneNumber.trim()) {
            newErrors.phoneNumber = "Phone number is required";
        }
        else if (!phoneNumber.match(/^\d{2,3}[\s]?\d{3}[\s]?\d{4}$/)) {
            newErrors.phoneNumber = "Invalid phone number format";
        }

        if (!email.trim()) {
            newErrors.email = "Email is required";
        }
        else if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            newErrors.email = "Invalid email format";
        }

        if (password.length < 6) {
            newErrors.password = "Password must be at least 6 characters long";
        }

        if (confirmPassword !== password) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(newErrors);

        try {

            if (Object.keys(newErrors).length === 0) {

                setIsLoading(true);
                await UserGateway.registerUser(email, password, firstName, lastName, phoneCode, phoneNumber);

                if (user) {
                    CartGateway.SaveCart(cart, user);
                    DeleteCookie(GetCartCookieKey);
                    updateCart();
                }

                console.log(`User registered: ${firstName} ${lastName}`);
                setUserDetails({ firstName, lastName, phoneCode, phoneNumber, messagePreference: messageType, messagePreferenceVerified: false });
                return true;
            }

        } catch (err: any) {
            let errorMessage = "An error occurred during registration.";
            switch (err.code) {
                case "auth/email-already-in-use":
                    newErrors.email = "The email address is already in use.";
                    break;
                case "auth/invalid-email":
                    newErrors.email = "The email address is not valid.";
                    break;
                case "auth/weak-password":
                    newErrors.password = "The password is too weak.";
                    break;
                default:
                    newErrors.email = err.message || errorMessage;
                    break;
            }
            setErrors(newErrors);
            console.error("Registration Error:", err);
        }
        finally {
            setIsLoading(false);
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleResend = async (messageType: MessageType) => {
        setMessageType(messageType);
        setVerifyPin("");
        await MessageGateway.SendPin({ toAddress: messageType === MessageType.Email ? email : Utils.FormatPhoneNumber(phoneCode, phoneNumber), messageType });
    }

    const handleVerify = async () => {
        let newErrors: { verifyPin?: string; } = {};
        if (!verifyPin.trim()) {
            newErrors.verifyPin = "Verify Pin is required";
        }
        else {
            const response = await MessageGateway.VerifyPin({ toAddress: messageType === MessageType.Email ? email : Utils.FormatPhoneNumber(phoneCode, phoneNumber), pin: verifyPin });
            if (response.ok) {
                setShowModal(FlagState.Complete);
                if (user) {
                    await UserGateway.UpdateUser(user, { firstName: firstName, lastName: lastName, phoneCode: phoneCode, phoneNumber: phoneNumber, messagePreference: messageType, messagePreferenceVerified: true });
                }
                handlePayment();
            }
            else {
                newErrors.verifyPin = "Invalid Pin"
            }
        }

        setErrors(newErrors);
    }

    const handlePayment = async () => {

        let newErrors: { firstName?: string; lastName?: string; email?: string; confirmPassword?: string; deliveryLocation?: string; phoneNumber?: string, paymentMethod?: string } = {};

        if (!firstName.trim()) {
            newErrors.firstName = "First name is required";
        }

        if (!lastName.trim()) {
            newErrors.lastName = "Last name is required";
        }

        if (!phoneNumber.trim() && !email.trim()) {
            newErrors.phoneNumber = "Phone number or email is required";
            newErrors.email = "Phone number or email is required";
        }

        if (phoneNumber.trim() && !phoneNumber.match(/^\d{2,3}[\s]?\d{3}[\s]?\d{4}$/)) {
            newErrors.phoneNumber = "Invalid phone number format";
        }

        if (email.trim() && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            newErrors.email = "Invalid email format";
        }

        // if (!user) {
        //     if (!email.trim()) {
        //         newErrors.email = "Email is required";
        //     }
        //     else if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        //         newErrors.email = "Invalid email format";
        //     }

        //     if (confirmEmail !== email) {
        //         newErrors.confirmEmail = "Emails do not match";
        //     }
        // }

        if (deliveryMethod === "delivery" && !deliveryLocation.trim()) {
            newErrors.deliveryLocation = "Delivery location is required";
        }

        if (settings.hasPrice && totalAmount > 0 && paymentMethod === PaymentType.None) {
            newErrors.paymentMethod = "Payment method is required";
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0 && enrichedCart && enrichedCart.length > 0 && settings) {
            setIsLoading(true);
            if (showModal !== FlagState.Complete && showModal !== FlagState.Show) {
                const sendWhatsApp = !!phoneNumber.trim() && (initialPhoneNumber !== Utils.FormatPhoneNumber(phoneCode, phoneNumber) || !userDetails?.messagePreferenceVerified);
                const sendEmail = !phoneNumber.trim() && !!email.trim() && (!user || !userDetails?.messagePreferenceVerified);
                if (sendWhatsApp || sendEmail) {
                    try {
                        const initialMessageType = sendWhatsApp ? MessageType.WhatsApp : MessageType.Email;
                        setMessageType(initialMessageType);
                        await MessageGateway.SendPin({ toAddress: sendWhatsApp ? Utils.FormatPhoneNumber(phoneCode, phoneNumber) : email, messageType: initialMessageType });
                    }
                    catch {
                        setErrors({ globalError: "Error sending Cellphone verify pin" });
                        setIsLoading(false);
                        return;
                    }
                    setVerifyPin("");
                    setShowModal(FlagState.Show);
                    return;
                }
            }

            try {
                const finalPaymentMethod = (settings && !settings.hasPrice) || (totalAmount === 0 && cart.cartItems.length > 0) ? PaymentType.NotApplicable : paymentMethod;
                const orderResponse = await OrderGateway.SaveOrder(enrichedCart, deliveryLocation, firstName, lastName, email, phoneCode, phoneNumber, comments, settings, messageType, finalPaymentMethod, cart.orderId ?? null);
                const orderResult = await orderResponse.json();
                const orderResponseModel = (orderResult) as OrderResponse;

                if (user)
                    await CartGateway.SaveCart({ ...cart, orderId: orderResponseModel.orderId }, user);
                else
                    SetCookie(GetCartCookieKey, JSON.stringify({ ...cart, orderId: orderResponseModel.orderId }), 7);

                updateCart();

                if (finalPaymentMethod === PaymentType.Yoco || finalPaymentMethod === PaymentType.Zapper || finalPaymentMethod === PaymentType.SnapScan) {

                    const checkoutRequest: CheckoutRequest = { amount: totalAmount, currency: settings.currency, orderId: orderResponseModel.orderId, orderNumber: orderResponseModel.orderNumber, failureUrl: `${document.location.origin}${Utils.GetUrl(`/checkout/orderfail/${orderResponseModel.orderId}`)}`, cancelUrl: `${document.location.origin}${Utils.GetUrl(`/checkout/ordercancel/${orderResponseModel.orderId}`)}`, successUrl: `${document.location.origin}${Utils.GetUrl(`/ordersuccess/${orderResponseModel.orderNumber}`)}`, origin: document.location.origin };
                    let response = null;
                    switch (finalPaymentMethod) {
                        case PaymentType.Zapper:
                            response = await PaymentGateway.ZapperCheckout(checkoutRequest);
                            break;
                        case PaymentType.Yoco:
                            response = await PaymentGateway.YocoCheckout(checkoutRequest);
                            break;
                        case PaymentType.SnapScan:
                            response = await PaymentGateway.SnapScanCheckout(checkoutRequest);
                            break;

                    }

                    if (response.ok) {
                        if (finalPaymentMethod === PaymentType.SnapScan) {
                            //Create to delete cart after user returns to site. Snapscan doesn't have a return url :(
                            SetCookie(SiteCookie.SnapScanRedirect, JSON.stringify({orderId: orderResponseModel.orderId, orderNumber: orderResponseModel.orderNumber, phoneCode, phoneNumber, email }), 0.003);
                        }

                        const result = await response.json();
                        const checkoutResponseModel = (result.data) as CheckoutResponse
                        setSnapScanUrl(checkoutResponseModel.redirectUrl);
                        setShowSnapScanModal(true);
                    }
                    else {
                        setErrors({ globalError: "Error Checking out!!" })
                    }
                }
                else {
                    navigate(Utils.GetUrl(`/ordersuccess/${orderResponseModel.orderNumber}`));
                }
            }
            catch (err) {
                errors.globalError = `Failed to Process Order - ${err}`;
                setErrors(errors);
                console.error("Failed to Process Order", err);
            }
            finally {

                setIsLoading(false);
            }
        }
    };

    const getPaymentOption = (paymentType: PaymentType) => {
        switch (paymentType) {
            case PaymentType.OnCollection:
                return (<span style={{ width: '180px' }}>
                    {deliveryMethod === "collection" ? `Pay on Collection` : `Pay on Delivery`}
                </span>);
            case PaymentType.Yoco:
                return (<><span style={{ width: '180px' }}>
                    Pay via Yoco
                </span>
                    <img src="/images/icons/yoco.png" alt="Yoco" height="25" style={{ marginRight: '8px' }} />
                    <img src="/images/icons/visa.png" alt="Visa" height="40" style={{ marginRight: '8px' }} />
                    <img src="/images/icons/mastercard.png" alt="Mastercard" height="40" /></>);
            case PaymentType.Zapper:
                return (<><span style={{ width: '180px' }}>
                    Pay via Zapper
                </span>
                    <img src="/images/icons/zapper.png" alt="Zapper" height="25" style={{ marginRight: '8px' }} />
                    {settings.checkoutPaymentOptions.indexOf("Yoco") < 0 && <>
                        <img src="/images/icons/visa.png" alt="Visa" height="40" style={{ marginRight: '8px' }} />
                        <img src="/images/icons/mastercard.png" alt="Mastercard" height="40" /></>}</>);
            case PaymentType.SnapScan:
                return (<><span style={{ width: '180px' }}>
                    Pay via SnapScan
                </span>
                    <img src="/images/icons/snapscan.png" alt="SnapScan" height="25" style={{ marginRight: '8px' }} />
                    {settings.checkoutPaymentOptions.indexOf("Yoco") < 0 && <>
                        <img src="/images/icons/visa.png" alt="Visa" height="40" style={{ marginRight: '8px' }} />
                        <img src="/images/icons/mastercard.png" alt="Mastercard" height="40" /></>}</>);
        }


    }

    const handlePageVisible = async () => {        
        if (isSnapScanLoading && cart.orderId) {
            var snapScanPaymentResult = await OrderGateway.GetSnapScanOrderSummary(cart.orderId, phoneCode, phoneNumber, email);
            if (snapScanPaymentResult && snapScanPaymentResult.status !== OrderStatus.PaymentPending) {
                DeleteCookie(SiteCookie.SnapScanRedirect);
                navigate(Utils.GetUrl(`/ordersuccess/${snapScanPaymentResult.orderNumber ?? snapScanPaymentResult.orderId}`));
            }
        }
    }

    const subtotal = enrichedCart ? enrichedCart.reduce((sum, item) => sum + item.quantity * item.price, 0) : 0;
    const totalAmount = subtotal + deliveryFee;
    const vatAmount = settings && settings.vatPercentage && settings.vatPercentage > 0 ? totalAmount - totalAmount / (1 + settings.vatPercentage / 100) : 0;

    return (
        <Container className="main-container mt-4">
            {errors.globalError && <Alert variant="danger">{errors.globalError}</Alert>}
            <h2 className="mb-4">Checkout</h2>
            <Row>
                {/* Left Section - User Details & Delivery */}
                <Col md={6}>
                    <Card className="p-3 shadow-sm">
                        <Card.Body>

                            <h5>User Details</h5>
                            <Form.Group className="mb-3">
                                <Form.Label>First Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter first name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    isInvalid={!!errors.firstName}
                                    disabled={!!userDetails}
                                />
                                <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Last Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    isInvalid={!!errors.lastName}
                                    disabled={!!userDetails}
                                />
                                <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Cellphone Number</Form.Label>
                                <Row className="g-2">
                                    <Col xs={12} md={6}>
                                        <Form.Select
                                            value={phoneCode}
                                            onChange={(e) => setPhoneCode(e.target.value)}
                                            disabled={!!userDetails && !!phoneNumber}
                                        >
                                            {CountryCodes.map((country, index) => (
                                                <option key={index} value={country.code}>
                                                    {country.name} ({country.code})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter cellphone number"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            isInvalid={!!errors.phoneNumber}
                                            disabled={!!userDetails && !!phoneNumber}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.phoneNumber}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Row>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email Address</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Enter email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    isInvalid={!!errors.email}
                                    disabled={!!userDetails}
                                />
                                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                            </Form.Group>
                            {!user && (
                                <>
                                    <p>
                                        <a href={Utils.GetUrl("/login/checkout")}>Already registered? Sign in here</a>
                                    </p>
                                    {settings && settings.allowEmailLogin && <>
                                        <h5>Registration (Optional)</h5>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Enter password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                isInvalid={!!errors.password}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Confirm Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Confirm password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                isInvalid={!!errors.confirmPassword}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Button className="mt-3" onClick={validateForm} disabled={isLoading} >
                                            {isLoading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Loading...
                                                </>
                                            ) : (
                                                "Register"
                                            )}
                                        </Button>
                                    </>}
                                </>
                            )}
                            {settings?.allowDelivery && <>
                                <h5 className="mt-3">Delivery Location</h5>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="radio"
                                        label="Collection (No delivery fee)"
                                        name="deliveryMethod"
                                        value="collection"
                                        checked={deliveryMethod === "collection"}
                                        onChange={() => { setDeliveryMethod("collection"); setDeliveryFee(0); }}
                                    />
                                    <Form.Check
                                        type="radio"
                                        label="Deliver to Location"
                                        name="deliveryMethod"
                                        value="delivery"
                                        checked={deliveryMethod === "delivery"}
                                        onChange={() => { setDeliveryMethod("delivery"); setDeliveryFee(settings && settings.allowDelivery ? Number(settings.deliveryCost) : 0); }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter delivery location"
                                        value={deliveryLocation}
                                        onChange={(e) => setDeliveryLocation(e.target.value)}
                                        disabled={deliveryMethod === "collection"}
                                        isInvalid={!!errors.deliveryLocation}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.deliveryLocation}</Form.Control.Feedback>
                                </Form.Group>
                            </>}
                            <h5 className="mt-3">Extra Comments</h5>
                            <Form.Group className="mb-3">
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Any additional comments..."
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Section - Order Summary & Payment */}
                <Col md={6}>
                    <Card className="p-3 shadow-sm">
                        <Card.Body>
                            <h5>Order Summary</h5>
                            {!enrichedCart || !settings ? (<Spinner animation="border" />) :
                                (<>
                                    <Table striped bordered hover responsive>
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Product</th>
                                                <th className="text-end">Qty</th>
                                                {settings.hasPrice && <th className="text-end">Total</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {enrichedCart.map((item) => (
                                                <tr key={ProductGateway.GetKey(item.id, item.attributes)}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            {item.imageUrl && <img src={item.imageUrl} alt={item.name} width="50" height="50" className="me-2" />}
                                                            <div>
                                                                <span>{item.name}</span>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {Object.entries(item.attributes).map(([key, value]) => `${key}: ${value}`).join(", ")}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end">{item.quantity}</td>
                                                    {settings.hasPrice && <td className="text-end">{Utils.FormatCurrency(item.price * item.quantity, settings.currency)}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    {settings.hasPrice && <>
                                        <hr />
                                        <div className="text-end">
                                            <p>Subtotal: <strong>{Utils.FormatCurrency(subtotal, settings.currency)}</strong></p>
                                            {settings.allowDelivery && <p>Delivery Fee: <strong>{Utils.FormatCurrency(Number(deliveryFee), settings.currency)}</strong></p>}
                                            <p>VAT ({settings.vatPercentage}%): <strong>{Utils.FormatCurrency(vatAmount, settings.currency)}</strong></p>
                                            <h5>Total: <strong>{Utils.FormatCurrency(Number(totalAmount), settings.currency)}</strong></h5>
                                        </div>
                                    </>}
                                </>)
                            }

                            {settings && settings.hasPrice && totalAmount !== 0 && cart.cartItems.length > 0 &&
                                <>
                                    <hr />
                                    <h5>Payment Method</h5>
                                    <div className="mb-3">
                                        {settings.checkoutPaymentOptions.filter(po => po.toLowerCase() !== PaymentType.Cash).map((paymentOption: string) => {
                                            return (<Form.Check
                                                type="radio"
                                                key={paymentOption}
                                                name="paymentMethod"
                                                value={paymentOption.toLowerCase()}
                                                checked={paymentMethod === paymentOption.toLowerCase()}
                                                onChange={(e) => setPaymentMethod(e.target.value as PaymentType)}
                                                className="mb-3"
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <Form.Check.Input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value={paymentOption.toLowerCase()}
                                                        checked={paymentMethod === paymentOption.toLowerCase()}
                                                        onChange={(e) => {
                                                            setErrors({ ...errors, paymentMethod: undefined });
                                                            setPaymentMethod(e.target.value as PaymentType);
                                                        }}
                                                    />
                                                    <Form.Check.Label htmlFor="credit" className="d-flex align-items-center mb-0">
                                                        {getPaymentOption(paymentOption.toLowerCase() as PaymentType)}
                                                    </Form.Check.Label>
                                                </div>
                                            </Form.Check>);
                                        })}
                                        {errors.paymentMethod && <div className="text-danger mt-1">{errors.paymentMethod}</div>}
                                    </div>

                                    <Button variant="primary" className="w-100 mt-3" onClick={handlePayment}>
                                        {isLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Loading...
                                            </>
                                        ) : (
                                            "Proceed"
                                        )}
                                    </Button>
                                </>
                            }
                            {((settings && !settings.hasPrice) || (totalAmount === 0 && cart.cartItems.length > 0)) &&
                                <>
                                    <Button variant="primary" className="w-100 mt-3" onClick={handlePayment} disabled={storeStatus !== ""}>
                                        {isLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Loading...
                                            </>
                                        ) : (
                                            "Place Order"
                                        )}
                                    </Button>
                                    <div className="w-100 mt-3">{storeStatus}</div>
                                </>
                            }
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showModal === FlagState.Show} onHide={() => { setIsLoading(false); setShowModal(FlagState.Hide); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Verify New User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You have entered a new phone number or email and it needs to be verified before placing an order.</p>
                    <p>Please enter your pin that was sent to <strong>{messageType === MessageType.WhatsApp ? Utils.FormatPhoneNumber(phoneCode, phoneNumber) : email}</strong></p>
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Enter verification pin"
                            value={verifyPin}
                            onChange={(e) => setVerifyPin(e.target.value)}
                            isInvalid={!!errors.verifyPin}
                        />
                        <Form.Control.Feedback type="invalid">{errors.verifyPin}</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleVerify}>Verify</Button>
                    {!!phoneNumber && <Button variant="outline-primary" onClick={() => handleResend(MessageType.WhatsApp)}>Resend pin via Whatsapp</Button>}
                    {!!email && <Button variant="secondary" onClick={() => handleResend(MessageType.Email)}>{`${!!phoneNumber ? "Send" : "Resend"} pin via Email`}</Button>}
                </Modal.Footer>
            </Modal>

            {/* SnapScan Payment Modal */}
            <PageVisibilityListener onVisible={handlePageVisible} />

            <Modal show={showSnapScanModal} onHide={() => { setShowSnapScanModal(false); setIsSnapScanLoading(false); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Pay with SnapScan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You have selected to pay with SnapScan. SnapScan does not automatically redirect you back to our site after payment completion.</p>
                    <p>Please <b>pay only once</b>, then return to our site to confirm your payment.</p>
                    <p>Click <b>Continue</b> to proceed to the SnapScan page, or <b>Cancel</b> to select another payment method.</p>
                </Modal.Body>
                <Modal.Footer>
                    <a
                        href={snapScanUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                        onClick={() => setIsSnapScanLoading(true)}
                    >
                        {isSnapScanLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading...
                            </>
                        ) : (
                            "Continue"
                        )}
                    </a>
                    <Button variant="secondary" onClick={() => { setShowSnapScanModal(false); setPaymentMethod(PaymentType.None); setIsSnapScanLoading(false); }}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
};

export default Checkout;